import { useState } from 'react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { FileText, Copy, ExternalLink, Loader2, X } from 'lucide-react';

export default function PurchaseOrderEmail({ manufacturerId, manufacturerName }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    const generate = async () => {
        setLoading(true);
        try {
            const r = await api.get(`/manufacturers/${manufacturerId}/po-email`);
            setData(r.data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Could not generate PO');
        } finally {
            setLoading(false);
        }
    };

    const copyAll = async () => {
        if (!data) return;
        const text = `Subject: ${data.subject}\nTo: ${data.to}\n\n${data.body}`;
        try {
            await navigator.clipboard.writeText(text);
            toast.success('PO copied to clipboard');
        } catch {
            toast.error('Copy failed');
        }
    };

    if (!data) {
        return (
            <button
                data-testid={`po-generate-${manufacturerId}`}
                onClick={generate}
                disabled={loading}
                className="inline-flex items-center gap-2 px-4 py-2 border border-zinc-700 hover:border-brand hover:text-brand font-display uppercase text-xs tracking-wider disabled:opacity-50"
            >
                {loading ? <Loader2 size={14} className="animate-spin" /> : <FileText size={14} />}
                {loading ? 'Generating PO…' : 'Generate PO Email'}
            </button>
        );
    }

    return (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
             data-testid={`po-modal-${manufacturerId}`}>
            <div className="bg-ink border border-zinc-800 max-w-3xl w-full max-h-[90vh] flex flex-col">
                <div className="flex items-center justify-between gap-3 px-5 py-3 border-b border-zinc-800 bg-surface">
                    <div className="flex items-center gap-2">
                        <FileText size={16} className="text-brand" />
                        <div>
                            <div className="font-display uppercase text-sm">Purchase Order · {manufacturerName}</div>
                            <div className="font-mono-data text-[10px] text-zinc-500">
                                {data.po_ref} · {data.item_count} SKUs · {data.total_units} units
                            </div>
                        </div>
                    </div>
                    <button onClick={() => setData(null)} className="p-1 text-zinc-400 hover:text-brand">
                        <X size={16} />
                    </button>
                </div>

                <div className="px-5 py-3 border-b border-zinc-800/60 font-mono-data text-xs space-y-1">
                    <div><span className="text-zinc-500">To:</span> <span className="text-zinc-200">{data.to}</span></div>
                    <div><span className="text-zinc-500">Subject:</span> <span className="text-zinc-200">{data.subject}</span></div>
                </div>

                <pre className="flex-1 px-5 py-4 font-mono-data text-xs text-zinc-300 whitespace-pre overflow-auto leading-relaxed">
{data.body}
                </pre>

                <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-zinc-800 bg-surface">
                    <button
                        data-testid={`po-copy-${manufacturerId}`}
                        onClick={copyAll}
                        className="inline-flex items-center gap-1.5 px-3 py-2 border border-zinc-700 hover:border-brand hover:text-brand font-display uppercase text-[10px] tracking-wider"
                    >
                        <Copy size={12} /> Copy
                    </button>
                    <a
                        data-testid={`po-mailto-${manufacturerId}`}
                        href={data.mailto}
                        className="inline-flex items-center gap-1.5 px-4 py-2 bg-brand hover:bg-brand-hover text-black font-display uppercase text-[10px] tracking-wider"
                    >
                        <ExternalLink size={12} /> Open in Mail App
                    </a>
                </div>
            </div>
        </div>
    );
}
